import { GeckoComponentCoords } from 'types/geometric/geckoComponentCoords'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoComponentSpace } from 'types/geometric/geckoComponentSpace'
import { parseNumber, parseString } from 'utils/parsingHelpers'

import { GeckoMarkupPrimitive } from './geckoMarkupPrimitive'

/**
 * Markup icons come from a fixed list of IDs (which correspond to specific
 * icons in the Edit Toolbar and 2D/3D visualization)
 */
export enum GeckoMarkupIconID {
  Camera = 'camera',
  Checkmark = 'checkmark',
  ExclamationMark = 'exclamation_mark',
  QuestionMark = 'question_mark',
}

/**
 * Markup icons are point-based primitives with a defined icon ID
 */
export class GeckoMarkupIcon extends GeckoMarkupPrimitive {
  //----------------------------------------------------------------------------

  public readonly kind = 'icon'
  public readonly icon_id: GeckoMarkupIconID
  public readonly coords: GeckoComponentCoords

  //----------------------------------------------------------------------------

  private constructor(
    id: string,
    markupLayerID: string,
    json: Record<string, unknown>,
    markupBounds: GeckoComponentSpace
  ) {
    const x = parseNumber(json.x)
    const y = parseNumber(json.y)
    if (x === undefined || y === undefined)
      throw new Error(
        `Primitive "${id}" has unknown coords: ${JSON.stringify(json)}`
      )
    const coords = GeckoComponentCoords.CreateFromValues(
      markupBounds.componentID,
      x,
      markupBounds.x.unit,
      y,
      markupBounds.y.unit
    )
    super(
      id,
      markupLayerID,
      GeckoComponentRect.CreateFromCoords(coords, coords)
    )

    const icon_id = parseString(json.icon_id)
    if (
      icon_id !== 'camera' &&
      icon_id !== 'checkmark' &&
      icon_id !== 'exclamation_mark' &&
      icon_id !== 'question_mark'
    )
      throw new Error(`Primitive "${id}" has unknown icon_id "${icon_id}"`)

    this.icon_id = icon_id as GeckoMarkupIconID
    this.coords = coords
  }

  //----------------------------------------------------------------------------

  public static CreateFromJSON(
    id: string,
    markupLayerID: string,
    json: Record<string, unknown>,
    markupBounds: GeckoComponentSpace
  ) {
    return new GeckoMarkupIcon(id, markupLayerID, json, markupBounds)
  }
}

/**
 * ---------------------------------------------------------------------------------------------------------------------
 *                                                    JSON-Type
 *           Copied from https://geckorobotics.atlassian.net/wiki/spaces/SE/pages/3958603801/Markup+Layer+Schema
 * ---------------------------------------------------------------------------------------------------------------------
 */
export type GeckoMarkupIconJSON = {
  kind: 'icon'
  icon_id: GeckoMarkupIconID
  x: number
  y: number
  metadata?: {
    type: string
    [field: string]: unknown
  }
}
